import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Heart, BookOpen, Users, Sparkles, Home, ExternalLink, Download, FileText, Youtube, Trophy, Star } from 'lucide-react';
import { toast } from 'sonner';
import KidsCurriculum from '../components/parent-resources/KidsCurriculum';

const ParentResources = ({ embedded = false }) => {
  const { API } = useContext(AuthContext);
  const [completedActivities, setCompletedActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [stats, setStats] = useState({ total_completed: 0, by_vital_sign: {} });

  useEffect(() => {
    fetchCompletedActivities();
    fetchStats();
  }, []);

  const fetchCompletedActivities = async () => {
    try {
      const response = await axios.get(`${API}/family-activities/completed`);
      setCompletedActivities(response.data);
    } catch (error) {
      console.error('Failed to load completed activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/family-activities/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const isActivityCompleted = (vitalSign, activityIndex) => {
    return completedActivities.some(
      a => a.vital_sign === vitalSign && a.activity_index === activityIndex
    );
  };

  const handleActivityToggle = async (vitalSign, activityIndex, activityText, isCurrentlyCompleted) => {
    try {
      if (isCurrentlyCompleted) {
        // Uncomplete the activity
        await axios.post(`${API}/family-activities/uncomplete`, {
          vital_sign: vitalSign,
          activity_index: activityIndex
        });
        setCompletedActivities(prev => 
          prev.filter(a => !(a.vital_sign === vitalSign && a.activity_index === activityIndex))
        );
        toast.success('Activity unchecked');
      } else {
        // Complete the activity
        const response = await axios.post(`${API}/family-activities/complete`, {
          vital_sign: vitalSign,
          activity_index: activityIndex,
          activity_text: activityText
        });
        
        setCompletedActivities(prev => [...prev, {
          vital_sign: vitalSign,
          activity_index: activityIndex,
          activity_text: activityText
        }]);
        
        // Show achievement notifications
        if (response.data.new_achievements && response.data.new_achievements.length > 0) {
          response.data.new_achievements.forEach(achievement => {
            toast.success(
              <div className="flex items-center gap-2">
                <span className="text-2xl">{achievement.icon}</span>
                <div>
                  <div className="font-semibold">Achievement Unlocked!</div>
                  <div className="text-sm">{achievement.name} - +{achievement.points} pts</div>
                </div>
              </div>
            );
          });
        } else {
          toast.success(`Activity completed! +${response.data.points_earned} points`);
        }
      }
      
      // Refresh stats
      fetchStats();
    } catch (error) {
      toast.error('Failed to update activity');
    }
  };

  const vitalSigns = [
    {
      key: 'pursue_god',
      title: 'Pursue God',
      icon: Heart,
      color: '#2B6B7F',
      discussion: {
        overview: 'Help your children develop a personal prayer life and relationship with God.',
        questions: [
          'What does it mean to talk to God? Can we talk to Him anytime?',
          'How do we know God listens to our prayers?',
          'What are some things we can thank God for today?',
          'How can we hear God speaking to us?'
        ],
        activities: [
          'Create a family prayer journal where everyone can draw or write prayers',
          'Start a bedtime prayer routine, letting each child pray in their own words',
          'Make a "Thank You God" jar - add notes throughout the week',
          'Take prayer walks together, talking to God about what you see'
        ]
      }
    },
    {
      key: 'engage_scripture',
      title: 'Engage Scripture',
      icon: BookOpen,
      color: '#5FA8C1',
      discussion: {
        overview: 'Make Bible reading fun and accessible for young children.',
        questions: [
          'Who wrote the Bible? (God used people to write His story!)',
          'What\'s your favorite Bible story so far?',
          'How does reading the Bible help us know God better?',
          'What did you learn about God from today\'s Bible story?'
        ],
        activities: [
          'Act out Bible stories with family members',
          'Use a children\'s Bible with pictures for storytime',
          'Memorize short verses together using songs or hand motions',
          'Create Bible story art - draw or color scenes from Scripture'
        ]
      }
    },
    {
      key: 'serve_others',
      title: 'Serve Others',
      icon: Users,
      color: '#7BA888',
      discussion: {
        overview: 'Teach children that loving God means loving and helping others.',
        questions: [
          'How did Jesus show love to people who needed help?',
          'What are ways we can help our family, friends, and neighbors?',
          'How does it feel when someone helps you? When you help someone?',
          'Who can we help this week?'
        ],
        activities: [
          'Make cards or drawings for elderly neighbors or church members',
          'Help prepare a meal together to share with someone in need',
          'Sort through toys to donate to children who need them',
          'Do acts of kindness as a family - rake leaves, carry groceries, etc.'
        ]
      }
    },
    {
      key: 'radiate_jesus',
      title: 'Radiate Jesus',
      icon: Sparkles,
      color: '#D4A574',
      discussion: {
        overview: 'Help children understand they can share God\'s love with others.',
        questions: [
          'How can we show others that we love Jesus?',
          'What makes Christians different from others? (We follow Jesus!)',
          'Who in your life doesn\'t know about Jesus yet?',
          'How can we be kind to everyone, even when it\'s hard?'
        ],
        activities: [
          'Practice inviting friends to church or church events',
          'Share how God answered a prayer with someone',
          'Show kindness to classmates or neighbors who seem lonely',
          'Pray together for friends and family who don\'t know Jesus'
        ]
      }
    }
  ];

  const faithDefinitions = [
    {
      term: 'Baptism',
      simpleDefinition: 'A special way to show that you love Jesus and want to follow Him. You go into water as a picture of your old life washing away, and come up clean to start a new life with Jesus!',
      icon: '💧',
      color: '#2B6B7F'
    },
    {
      term: 'Communion',
      simpleDefinition: 'A special meal where we remember Jesus. The bread reminds us of Jesus\' body, and the juice reminds us of His blood. Jesus gave these to us on the night before He died to help us remember how much He loves us.',
      icon: '🍞',
      color: '#5FA8C1'
    },
    {
      term: 'Church',
      simpleDefinition: 'Church is not just a building - it\'s people! When we talk about "the church," we mean all the people who love and follow Jesus. We meet in a building, but the real church is the family of God\'s people everywhere!',
      icon: '👨‍👩‍👧‍👦',
      color: '#7BA888'
    },
    {
      term: 'Worship',
      simpleDefinition: 'Worship means showing God how much we love Him. We can worship by singing songs to Him, praying, reading the Bible, or just telling Him we love Him. When we worship together with other Christians, it\'s called "communal worship" - which means we\'re all praising God as one big family!',
      icon: '🎵',
      color: '#D4A574'
    }
  ];

  const externalResources = [
    {
      title: 'Bible App for Kids',
      description: 'Interactive Bible stories and activities for children',
      url: 'https://www.bible.com/kids',
      category: 'Digital Resources'
    },
    {
      title: 'Bible Fun Land',
      description: 'Interactive Bible games, puzzles, and activities for children',
      url: 'https://biblefunland.com/biblefunland/index.html',
      category: 'Games & Activities'
    },
    {
      title: 'Seeds Family Worship',
      description: 'Scripture memory songs for kids and families',
      url: 'https://seedsfamilyworship.com/',
      category: 'Music & Memory'
    },
    {
      title: 'Focus on the Family',
      description: 'Parenting tips and family devotional resources',
      url: 'https://www.focusonthefamily.com/parenting/',
      category: 'Parenting Support'
    },
    {
      title: 'Gospel Project for Kids',
      description: 'Christ-centered curriculum connecting the Bible story',
      url: 'https://www.gospelproject.com/kids/',
      category: 'Curriculum'
    }
  ];

  const prayerPrompts = [
    {
      time: 'Morning Prayer',
      prompt: 'Good morning, God! Thank you for this new day. Help us to love You and others today. Amen.'
    },
    {
      time: 'Mealtime Prayer',
      prompt: 'Thank You, God, for this food. Thank You for our family. Help us to share Your love with others. Amen.'
    },
    {
      time: 'Bedtime Prayer',
      prompt: 'Dear God, thank You for today. Thank You for keeping us safe. Help us to sleep well and wake up ready to serve You. Amen.'
    },
    {
      time: 'Gratitude Prayer',
      prompt: 'God, we are so thankful for [let each family member share]. You give us good gifts! Amen.'
    },
    {
      time: 'Prayer for Others',
      prompt: 'Dear Jesus, please be with [friend/family member\'s name]. Help them to feel Your love. Show us how we can help them. Amen.'
    }
  ];

  // Inner content (tabs + bottom CTA) so it can render inside another page (e.g. Resources)
  const inner = (
    <>
      <Tabs defaultValue="vital-signs" className="space-y-6">
          <TabsList className="grid grid-cols-2 md:grid-cols-6 h-auto gap-2 bg-transparent">
            <TabsTrigger value="vital-signs" className="data-[state=active]:bg-white">
              Vital Signs Guides
            </TabsTrigger>
            <TabsTrigger value="kids-curriculum" className="data-[state=active]:bg-white">
              Kids Curriculum
            </TabsTrigger>
            <TabsTrigger value="teens" className="data-[state=active]:bg-white">
              Teens
            </TabsTrigger>
            <TabsTrigger value="definitions" className="data-[state=active]:bg-white">
              Faith Definitions
            </TabsTrigger>
            <TabsTrigger value="prayers" className="data-[state=active]:bg-white">
              Prayer Prompts
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-white">
              External Links
            </TabsTrigger>
          </TabsList>

          {/* Kids Curriculum Tab */}
          <TabsContent value="kids-curriculum">
            <KidsCurriculum />
          </TabsContent>

          {/* Vital Signs Discussion Guides */}
          <TabsContent value="vital-signs" className="space-y-6">
            <div className="mb-4 text-center">
              <h2 className="text-2xl font-heading font-bold text-[#2B6B7F] mb-2">
                Discussion Guides for Each Vital Sign
              </h2>
              <p className="text-slate-600">
                Help your children understand and practice the four spiritual vital signs
              </p>
            </div>

            {/* Progress Card */}
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">Family Activities Progress</h3>
                      <p className="text-sm text-slate-600">
                        Complete activities to earn points and achievements!
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-amber-600">{stats.total_completed}/16</div>
                    <div className="text-sm text-slate-500">Activities Done</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-4 h-3 bg-amber-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-500"
                    style={{ width: `${(stats.total_completed / 16) * 100}%` }}
                  />
                </div>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                  {['pursue_god', 'engage_scripture', 'serve_others', 'radiate_jesus'].map((vs, idx) => {
                    const colors = ['#2B6B7F', '#5FA8C1', '#7BA888', '#D4A574'];
                    const count = stats.by_vital_sign?.[vs] || 0;
                    return (
                      <div key={vs} className="p-2 rounded bg-white/50">
                        <div className="font-semibold" style={{ color: colors[idx] }}>{count}/4</div>
                        <div className="text-slate-500 truncate">{vs.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()).split(' ')[0]}</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Inner Tabs for Each Vital Sign */}
            <Tabs defaultValue="pursue_god" className="space-y-4">
              <TabsList className="grid w-full grid-cols-4 h-auto gap-1">
                {vitalSigns.map((sign) => {
                  const Icon = sign.icon;
                  const completedCount = stats.by_vital_sign?.[sign.key] || 0;
                  return (
                    <TabsTrigger 
                      key={sign.key}
                      value={sign.key} 
                      className="flex flex-col items-center gap-1 py-3 px-2 h-auto text-center relative"
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: sign.color }} />
                      <span className="text-xs sm:text-sm leading-tight whitespace-pre-line">
                        {sign.title.replace(' ', '\n')}
                      </span>
                      {completedCount === 4 && (
                        <Star className="w-3 h-3 text-amber-500 absolute top-1 right-1" fill="currentColor" />
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {vitalSigns.map((sign) => {
                const Icon = sign.icon;
                return (
                  <TabsContent key={sign.key} value={sign.key}>
                    <Card className="border-t-4" style={{ borderTopColor: sign.color }}>
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" 
                               style={{ backgroundColor: `${sign.color}20` }}>
                            <Icon className="w-6 h-6" style={{ color: sign.color }} />
                          </div>
                          <div>
                            <CardTitle className="text-xl" style={{ color: sign.color }}>{sign.title}</CardTitle>
                            <CardDescription className="mt-1">{sign.discussion.overview}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div>
                          <h4 className="font-semibold text-slate-800 mb-3">Discussion Questions:</h4>
                          <ul className="space-y-3">
                            {sign.discussion.questions.map((q, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" 
                                     style={{ backgroundColor: sign.color }}>
                                  {idx + 1}
                                </div>
                                <span className="pt-0.5">{q}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="border-t pt-6">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-800">Family Activities:</h4>
                            <span className="text-sm text-slate-500">
                              {stats.by_vital_sign?.[sign.key] || 0}/4 completed
                            </span>
                          </div>
                          <ul className="space-y-3">
                            {sign.discussion.activities.map((activity, idx) => {
                              const isCompleted = isActivityCompleted(sign.key, idx);
                              return (
                                <li 
                                  key={idx} 
                                  className={`flex items-start gap-3 text-sm p-3 rounded-lg transition-all cursor-pointer ${
                                    isCompleted 
                                      ? 'bg-green-50 border border-green-200' 
                                      : 'bg-slate-50 hover:bg-slate-100'
                                  }`}
                                  onClick={() => handleActivityToggle(sign.key, idx, activity, isCompleted)}
                                >
                                  <Checkbox 
                                    checked={isCompleted}
                                    className="mt-0.5 flex-shrink-0"
                                    style={{ 
                                      borderColor: isCompleted ? '#22c55e' : sign.color,
                                      backgroundColor: isCompleted ? '#22c55e' : 'transparent'
                                    }}
                                  />
                                  <span className={isCompleted ? 'text-slate-500 line-through' : 'text-slate-700'}>
                                    {activity}
                                  </span>
                                  {isCompleted && (
                                    <span className="ml-auto text-xs text-green-600 font-medium">+5 pts</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                );
              })}
            </Tabs>
          </TabsContent>

          {/* Faith Definitions */}
          <TabsContent value="definitions" className="space-y-6">
            <Card className="border-t-4 border-t-[#5FA8C1]">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Simple Faith Definitions for Kids
                </CardTitle>
                <CardDescription>
                  Age-appropriate explanations to help children understand important faith concepts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {faithDefinitions.map((item, idx) => (
                  <Card key={idx} className="border-l-4" style={{ borderLeftColor: item.color }}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="text-4xl flex-shrink-0">{item.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-xl font-heading font-semibold mb-2" style={{ color: item.color }}>
                            {item.term}
                          </h3>
                          <p className="text-slate-700 leading-relaxed">{item.simpleDefinition}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-slate-700">
                    <strong>Tip:</strong> Use these definitions when your children ask questions. Don't worry about using "big church words" - keep it simple and conversational!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prayer Prompts */}
          <TabsContent value="prayers" className="space-y-6">
            <Card className="border-t-4 border-t-[#7BA888]">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Family Prayer Prompts
                </CardTitle>
                <CardDescription>
                  Simple prayers to help establish a rhythm of prayer in your home
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {prayerPrompts.map((prompt, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-[#2B6B7F] mb-2">{prompt.time}</h4>
                      <p className="text-slate-700 italic leading-relaxed">{prompt.prompt}</p>
                    </CardContent>
                  </Card>
                ))}
                
                <div className="mt-6 p-4 rounded-lg bg-[#D4A574]/10 border border-[#D4A574]/30">
                  <p className="text-sm text-slate-700 mb-3">
                    <strong>Encourage Your Kids:</strong> These are starter prayers! As your children grow, encourage them to pray in their own words. God loves to hear their hearts!
                  </p>
                  <p className="text-sm text-slate-700">
                    <strong>Remember:</strong> Prayer doesn't have to be formal or fancy. It's just talking to God like you'd talk to a loving parent or best friend.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* External Resources */}
          <TabsContent value="resources" className="space-y-6">
            <Card className="border-t-4 border-t-[#D4A574]">
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
                  Helpful External Resources
                </CardTitle>
                <CardDescription>
                  Trusted websites and tools to support your family's spiritual growth
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* YouTube Playlist Link Card */}
                <a
                  href="https://www.youtube.com/playlist?list=PL5aPdmniG3y_n7hXEKTV4qQnIeCe-p6Ws"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block p-5 border-2 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors">
                      <Youtube className="w-7 h-7 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-lg text-slate-800 group-hover:text-red-700">
                          Parent Resources Video Playlist
                        </h4>
                        <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-red-600" />
                      </div>
                      <p className="text-sm text-slate-600 mb-2">
                        Watch helpful videos for parents on faith, family, and raising children in Christ
                      </p>
                      <span className="text-sm text-red-600 font-medium">Open playlist on YouTube →</span>
                    </div>
                  </div>
                </a>

                {externalResources.map((resource, idx) => (
                  <Card key={idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-[#2B6B7F]">{resource.title}</h4>
                            <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded-full">
                              {resource.category}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 mb-3">{resource.description}</p>
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-[#2B6B7F] hover:text-[#1E4D5F]"
                          >
                            Visit Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <div className="mt-6 p-4 rounded-lg bg-yellow-50 border border-yellow-200">
                  <p className="text-sm text-slate-700">
                    <strong>Note:</strong> These external resources are provided as helpful tools. Please review content to ensure it aligns with your family's values and faith tradition.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Teens Resources Tab */}
          <TabsContent value="teens" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Teen Resources</CardTitle>
                <CardDescription>
                  Resources to help parents navigate the teenage years with biblical wisdom and practical guidance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mobile Notice */}
                <div className="md:hidden p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-slate-700">
                    <strong>📱 Mobile Tip:</strong> Tap "View PDF" to open the document in your browser, 
                    or tap "Download" to save it to your device for better viewing.
                  </p>
                </div>

                {/* Life Map - Faith */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-[#2B6B7F] to-[#5FA8C1] p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-white flex-shrink-0" />
                        <h3 className="font-semibold text-white text-base sm:text-lg">Life Map: Faith</h3>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/zktsbt8c_PC_Resource_2023_Life-Map-Faith.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View PDF</span>
                        </a>
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/zktsbt8c_PC_Resource_2023_Life-Map-Faith.pdf"
                          download="Life-Map-Faith.pdf"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Hide iframe on mobile, show on desktop */}
                  <div className="bg-white hidden md:block">
                    <iframe
                      src="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/zktsbt8c_PC_Resource_2023_Life-Map-Faith.pdf"
                      title="Life Map: Faith"
                      className="w-full border-0"
                      style={{ height: '600px' }}
                    />
                  </div>
                </div>

                {/* Life Map - Sexual Integrity */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-[#7BA888] to-[#5FA8C1] p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-white flex-shrink-0" />
                        <h3 className="font-semibold text-white text-base sm:text-lg">Life Map: Sexual Integrity</h3>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/ymy4gzq3_PC_Resource_2023_Life-Map-Sexual-Integrity.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View PDF</span>
                        </a>
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/ymy4gzq3_PC_Resource_2023_Life-Map-Sexual-Integrity.pdf"
                          download="Life-Map-Sexual-Integrity.pdf"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Hide iframe on mobile, show on desktop */}
                  <div className="bg-white hidden md:block">
                    <iframe
                      src="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/ymy4gzq3_PC_Resource_2023_Life-Map-Sexual-Integrity.pdf"
                      title="Life Map: Sexual Integrity"
                      className="w-full border-0"
                      style={{ height: '600px' }}
                    />
                  </div>
                </div>

                {/* Life Map - Technology */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-[#D4A574] to-[#7BA888] p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-white flex-shrink-0" />
                        <h3 className="font-semibold text-white text-base sm:text-lg">Life Map: Technology</h3>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/f3z315fg_PC_Resource_2023_Life-Map-Technology.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View PDF</span>
                        </a>
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/f3z315fg_PC_Resource_2023_Life-Map-Technology.pdf"
                          download="Life-Map-Technology.pdf"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Hide iframe on mobile, show on desktop */}
                  <div className="bg-white hidden md:block">
                    <iframe
                      src="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/f3z315fg_PC_Resource_2023_Life-Map-Technology.pdf"
                      title="Life Map: Technology"
                      className="w-full border-0"
                      style={{ height: '600px' }}
                    />
                  </div>
                </div>

                {/* Phases Book */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-purple-600 to-[#2B6B7F] p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-white flex-shrink-0" />
                        <h3 className="font-semibold text-white text-base sm:text-lg">Phases Book</h3>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/uwabp3eq_phases%20book.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span>View PDF</span>
                        </a>
                        <a
                          href="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/uwabp3eq_phases%20book.pdf"
                          download="Phases-Book.pdf"
                          className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          <span>Download</span>
                        </a>
                      </div>
                    </div>
                  </div>
                  {/* Hide iframe on mobile, show on desktop */}
                  <div className="bg-white hidden md:block">
                    <iframe
                      src="https://customer-assets.emergentagent.com/job_discipleship-journey/artifacts/uwabp3eq_phases%20book.pdf"
                      title="Phases Book"
                      className="w-full border-0"
                      style={{ height: '600px' }}
                    />
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-sm text-slate-700">
                    <strong>About These Resources:</strong> These Life Maps and the Phases Book provide biblical guidance 
                    for navigating the teenage years. They cover critical topics like faith development, sexual integrity, 
                    and technology use, helping parents have meaningful conversations with their teens.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bottom CTA */}
        <Card className="mt-8 bg-gradient-to-br from-[#2B6B7F] to-[#5FA8C1] text-white">
          <CardContent className="pt-6 text-center">
            <h3 className="text-2xl font-heading font-bold mb-3">
              You're Not Alone in This Journey
            </h3>
            <p className="text-blue-50 max-w-2xl mx-auto">
              Parenting is one of the most important callings God gives us. Remember, you don't have to be perfect - 
              just faithful. The church family at WFMC is here to support you every step of the way!
            </p>
          </CardContent>
        </Card>
    </>
  );

  // Embedded mode: render just the inner content (used inside the Resources page)
  if (embedded) {
    return (
      <div className="space-y-6" data-testid="parent-resources-embedded">
        {inner}
      </div>
    );
  }

  // Standalone mode: full page chrome
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50" data-testid="parent-resources-page">
      <Navigation />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8 animate-fade-in text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2B6B7F] to-[#5FA8C1] flex items-center justify-center mx-auto mb-4">
            <Home className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#2B6B7F] mb-3">
            Parent Resources
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Tools and guidance to help your family grow in faith together
          </p>
        </div>

        {inner}
      </div>
    </div>
  );
};

export default ParentResources;
